import { Box, Tooltip } from '@mui/material';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Select } from 'components/common';
import { PartnershipTagBadge, RemovePartnershipButton } from './PartnershipSectionComponents';
import AdSettingsFieldsSection from './AdSettingsFieldsSection';
import type { AdSetEntry } from '../api/getAdSets';

export interface PartnershipOption {
  value: string;
  label: string;
  tag: string;
  facebookPageId: string;
  appPageId: string;
  appPageName: string;
  instagramPageId: string;
}

export interface PartnershipAdSettingFormValue {
  partnershipId: string;
  partnershipTag: string;
  partnershipPartnerPageId: string;
  partnershipAppPageId: string;
  partnershipPartnerInstagramId: string | null;
}

interface PartnershipSectionProps {
  index: number;
  partnershipOptions: PartnershipOption[];
  canRemove: boolean;
  onRemove: () => void;
}

const PartnershipSection: FC<PartnershipSectionProps> = ({
  index,
  partnershipOptions,
  canRemove,
  onRemove,
}) => {
  const fieldName = useMemo(() => `partnershipAdSettings.${index}`, [index]);
  const { setValue } = useFormContext();
  const app = useWatch({ name: 'app' }) as string;
  const platform = useWatch({ name: 'platform' }) as string;
  const selectedAdSetsRows = useWatch({ name: 'selectedAdSets' }) as AdSetEntry[];
  const selectedPartnershipId = useWatch({
    name: `${fieldName}.partnershipId`,
  }) as string | undefined;
  const partnershipAppPageId = useWatch({
    name: `${fieldName}.partnershipAppPageId`,
  }) as string | undefined;

  const adSets = useMemo(
    () =>
      selectedAdSetsRows?.map(({ account_id, name, id, campaign_id }) => ({
        account_id,
        name,
        id,
        campaign_id,
      })) ?? [],
    [selectedAdSetsRows],
  );

  const partnershipOptionsById = useMemo(
    () => new Map(partnershipOptions.map((partnership) => [partnership.value, partnership])),
    [partnershipOptions],
  );
  const selectedPartnership = useMemo(
    () => {
      if (!selectedPartnershipId?.trim()) return null;
      return partnershipOptionsById.get(selectedPartnershipId) ?? null;
    },
    [selectedPartnershipId, partnershipOptionsById],
  );

  useEffect(() => {
    setValue(`${fieldName}.partnershipTag`, selectedPartnership?.tag ?? '', {
      shouldValidate: true,
    });
    setValue(
      `${fieldName}.partnershipPartnerPageId`,
      selectedPartnership?.facebookPageId ?? '',
      { shouldValidate: true },
    );
    setValue(
      `${fieldName}.partnershipAppPageId`,
      selectedPartnership?.appPageId ?? '',
      { shouldValidate: true },
    );
    setValue(
      `${fieldName}.partnershipPartnerInstagramId`,
      selectedPartnership?.instagramPageId ?? null,
      { shouldValidate: true },
    );
  }, [fieldName, selectedPartnership, setValue]);

  const hasTag = Boolean(selectedPartnership?.tag?.trim());
  const showHeaderRow = hasTag || canRemove;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f8f8',
        borderRadius: '12px',
        p: 2,
        gap: 2,
        '& .remove-partnership-button': {
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 120ms ease-in-out',
        },
        '&:hover .remove-partnership-button': {
          opacity: 1,
          pointerEvents: canRemove ? 'auto' : 'none',
        },
      }}
    >
      {showHeaderRow && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: 28,
            mb: 1,
          }}
        >
          <PartnershipTagBadge tag={selectedPartnership?.tag ?? '-'} />
          {canRemove && (
            <Box sx={{ ml: 'auto' }}>
              <RemovePartnershipButton canRemove={canRemove} onRemove={onRemove} />
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Select
            name={`partnershipAdSettings.${index}.partnershipId`}
            options={partnershipOptions}
            asAutocomplete
            inputLabel={'Partnerships'}
            placeholder={'Partnership page'}
            inputSx={{
              '&.MuiOutlinedInput-root': {
                maxHeight: '40px',
                p: '0 9px',
              },
            }}
            renderOption={(option) => {
              const partnership = option as PartnershipOption;

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 2,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {partnership.label}
                  </Box>
                  <Tooltip title={partnership.appPageName}>
                    <Box
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        flexShrink: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '50%',
                      }}
                    >
                      {partnership.appPageName}
                    </Box>
                  </Tooltip>
                </Box>
              );
            }}
            required
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Select
            name={`partnershipAdSettings.${index}.partnershipAppPageId`}
            inputLabel={'Facebook page'}
            options={[
              {
                value: selectedPartnership?.appPageId ?? '',
                label: selectedPartnership?.appPageName ?? '-',
              },
            ]}
            disabled
            asAutocomplete
            inputSx={{
              '&.MuiOutlinedInput-root': {
                maxHeight: '40px',
                p: '0 9px',
              },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          gap: 1
        }}
      >
        <AdSettingsFieldsSection
          settingsIndex={index}
          app={app}
          platform={platform}
          adSets={adSets}
          isPartnershipAds
          partnershipId={selectedPartnershipId}
          appPageId={partnershipAppPageId}
        />
      </Box>
    </Box>
  );
};

export default PartnershipSection;
